import { useCallback, useMemo } from "react";
import {
  AuthScopeBase,
  AvengersRole,
  BrandDifferentiatedAuthScope,
  CrudOp,
  DifferentiationType,
  ProjectDifferentiatedAuthScope,
  UndifferentiatedAuthScope,
} from "./authHelperTypes";
import { useExtractAssignedRoles } from "./useExtractAssignedRoles";

interface AuthHelperBase {
  assignedBrands: (crudOp: CrudOp) => Set<string>;
  assignedProjects: (crudOp: CrudOp) => Set<string>;
}

export interface BrandDifferentiatedAuthHelper extends AuthHelperBase {
  evaluate: (crudOp: CrudOp, brand: string) => boolean;
}

export interface ProjectDifferentiatedAuthHelper extends AuthHelperBase {
  evaluate: (crudOp: CrudOp, project: string) => boolean;
}

export interface UndifferentiatedAuthHelper extends AuthHelperBase {
  evaluate: (crudOp: CrudOp) => boolean;
}

export type AuthHelperCreator = typeof useAuthHelper;
type GenericAuthScope = Parameters<AuthHelperCreator>[0];

export function useAuthHelper(scope: BrandDifferentiatedAuthScope): BrandDifferentiatedAuthHelper;
export function useAuthHelper(scope: ProjectDifferentiatedAuthScope): ProjectDifferentiatedAuthHelper;
export function useAuthHelper(scope: UndifferentiatedAuthScope): UndifferentiatedAuthHelper;
export function useAuthHelper(scope: AuthScopeBase): AuthHelperBase {
  const authHelperCreator = useAuthHelperCreator();
  return useMemo(
    () => authHelperCreator(scope as GenericAuthScope),
    [authHelperCreator, scope],
  );
}

/**
 * Always prefer the useAuthHelper method, unless you need to create auth helpers inside a loop or something.
 * The API of useAuthHelper is more user-friendly for all use cases, where the scope is known.
 */
export function useAuthHelperCreator(): AuthHelperCreator {
  const assignedRoles = useExtractAssignedRoles();

  const getAuthorizedRoles = useCallback(
    (scope: AuthScopeBase, crudOp: CrudOp) => {
      return new Set(Object.entries(scope.mappings)
        .filter(([, it]) => it.includes(crudOp))
        .map(([role]) => role as AvengersRole));
    },
    [],
  );

  const getAssignedBrands = useCallback(
    (scope: AuthScopeBase, crudOp: CrudOp) => {
      const authorizedRoles = getAuthorizedRoles(scope, crudOp);
      return new Set(assignedRoles.filter(({ role }) => authorizedRoles.has(role))
        .flatMap((it) => it.brands ?? []));
    },
    [assignedRoles, getAuthorizedRoles],
  );
  const getAssignedProjects = useCallback(
    (scope: AuthScopeBase, crudOp: CrudOp) => {
      const authorizedRoles = getAuthorizedRoles(scope, crudOp);
      return new Set(assignedRoles.filter(({ role }) => authorizedRoles.has(role))
        .flatMap((it) => it.projects ?? []));
    },
    [assignedRoles, getAuthorizedRoles],
  );

  return useCallback(
    (scope: AuthScopeBase) => {
      const assignedBrands: AuthHelperBase["assignedBrands"] = (crudOp) => {
        return getAssignedBrands(scope, crudOp);
      };
      const assignedProjects: AuthHelperBase["assignedProjects"] = (crudOp) => {
        return getAssignedProjects(scope, crudOp);
      };

      switch (scope.type) {
        case DifferentiationType.BRAND: {
          const evaluate: BrandDifferentiatedAuthHelper["evaluate"] = (crudOp, brand) => {
            const authorizedRoles = getAuthorizedRoles(scope, crudOp);
            return assignedRoles.some(({ role, brands }) => {
              return authorizedRoles.has(role) && (brands ?? []).includes(brand);
            });
          };

          return { assignedBrands, assignedProjects, evaluate };
        }
        case DifferentiationType.PROJECT: {
          const evaluate: ProjectDifferentiatedAuthHelper["evaluate"] = (crudOp, project) => {
            const authorizedRoles = getAuthorizedRoles(scope, crudOp);
            return assignedRoles.some(({ role, projects }) => {
              return authorizedRoles.has(role) && (projects ?? []).includes(project);
            });
          };

          return { assignedBrands, assignedProjects, evaluate };
        }
        case DifferentiationType.NONE: {
          const evaluate: UndifferentiatedAuthHelper["evaluate"] = (crudOp) => {
            const authorizedRoles = getAuthorizedRoles(scope, crudOp);
            return assignedRoles.some(({ role }) => authorizedRoles.has(role));
          };

          return { assignedBrands, assignedProjects, evaluate };
        }
      }
    },
    [getAssignedBrands, getAssignedProjects, assignedRoles, getAuthorizedRoles],
  ) as AuthHelperCreator;
}
